import { useCallback, useEffect, useMemo, useState } from 'react';

export const getVariantOption = (variant, optionName) =>
  variant?.selectedOptions?.find((option) => option.name === optionName)?.value;

const fallbackVariant = (product) => ({ title: 'M', available: true, price: product?.price });

const galleryForVariant = (product, selected) => {
  const media = product?.media?.length ? product.media : (product?.images || []).map((url) => ({ id: url, url }));
  const selectedImage = selected?.imageUrl || media.find((image) => image.variantIds?.includes(selected?.id))?.url;
  return [...media].sort((first, second) => Number(second.url === selectedImage) - Number(first.url === selectedImage));
};

const availableVariants = (product) => (
  product?.variants?.length ? product.variants : [fallbackVariant(product)]
);

const optionByName = (product, pattern) => (
  product?.options?.find((option) => pattern.test(option.name))
);

const findSelectedVariant = ({ variants, colorOption, sizeOption, activeColor, activeSize, firstAvailable }) => (
  variants.find((variant) =>
    (!colorOption || getVariantOption(variant, colorOption.name) === activeColor)
    && (!sizeOption || getVariantOption(variant, sizeOption.name) === activeSize)
  )
  || variants.find((variant) => !colorOption || getVariantOption(variant, colorOption.name) === activeColor)
  || firstAvailable
);

const findColorVariant = ({ variants, colorName, sizeOption, activeSize, color }) => (
  variants.find((variant) =>
    getVariantOption(variant, colorName) === color
    && (!sizeOption || getVariantOption(variant, sizeOption.name) === activeSize)
    && variant.available
  ) || variants.find((variant) => getVariantOption(variant, colorName) === color)
);

export function useProductVariant(product) {
  const variants = useMemo(() => availableVariants(product), [product]);
  const firstAvailable = useMemo(
    () => variants.find((variant) => variant.available) || variants[0],
    [variants]
  );
  const colorOption = useMemo(() => optionByName(product, /colou?r/i), [product]);
  const sizeOption = useMemo(() => optionByName(product, /size/i), [product]);
  const [choice, setChoice] = useState({ color: null, size: null });

  useEffect(() => {
    setChoice({ color: null, size: null });
  }, [product?.id]);

  const activeColor = choice.color || getVariantOption(firstAvailable, colorOption?.name);
  const activeSize = choice.size || getVariantOption(firstAvailable, sizeOption?.name);
  const selected = useMemo(() => findSelectedVariant({
    variants, colorOption, sizeOption, activeColor, activeSize, firstAvailable,
  }), [activeColor, activeSize, colorOption, firstAvailable, sizeOption, variants]);

  const selectColor = useCallback((color) => {
    const matchingVariant = findColorVariant({
      variants, colorName: colorOption?.name, sizeOption, activeSize, color,
    });
    setChoice({ color, size: sizeOption ? getVariantOption(matchingVariant, sizeOption.name) : null });
  }, [activeSize, colorOption?.name, sizeOption, variants]);

  const selectSize = useCallback((size) => setChoice((previous) => ({ ...previous, size })), []);
  const selectVariant = useCallback((variant) => setChoice({
    color: getVariantOption(variant, colorOption?.name),
    size: getVariantOption(variant, sizeOption?.name),
  }), [colorOption?.name, sizeOption?.name]);
  const images = useMemo(() => galleryForVariant(product, selected), [product, selected]);

  return { variants, colorOption, sizeOption, activeColor, activeSize, selected, images, selectColor, selectSize, selectVariant };
}